/**
 * ReportShell — shared inner wrapper for all report pages.
 *
 * Provides consistent layout: title, subtitle, actions slot, and content area.
 * This is NOT the page shell (that's Shell.tsx with the sidebar). This
 * standardizes the report header pattern so every report page looks and
 * behaves the same without duplicating layout code.
 *
 * Time filtering is handled by the global UnifiedTimeFilter (rendered by each
 * page in its actions slot or header). The old week dropdown props have been
 * removed.
 */

import type { ReactNode } from 'react';
import { shortDate } from '../format';

export interface ReportShellProps {
  /** Page title */
  title: string;
  /** Subtitle / description */
  subtitle?: string;
  /** Currently selected week ending date, shown in the subtitle */
  weekEnding?: string | null | undefined;
  /** Slot for export button, time filter, or other header actions */
  actions?: ReactNode | undefined;
  /** The report content */
  children: ReactNode;
}

export function ReportShell({ title, subtitle, weekEnding, actions, children }: ReportShellProps) {
  return (
    <>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-ink">{title}</h1>
          <p className="mt-1 text-sm text-ink-faint">
            {subtitle}
            {weekEnding && (
              <>
                {subtitle ? ' · ' : ''}
                Week ending{' '}
                <span className="font-medium text-ink-muted">{shortDate(weekEnding)}</span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">{actions}</div>
      </div>
      {children}
    </>
  );
}
